import { useCallback } from 'react';
import { db } from '../db';
import { MUTATION_HANDLERS, MUTATION_LAYER } from '../services/mutationHandlers';
import { generateMutationKey, MUTATION_STATUS } from '../services/mutationLog';
import { triggerDrain } from '../services/sync';

/**
 * Local-first mutation hook.
 *
 * Returns `{ mutate }` where `mutate({ type, payload })`:
 *   1. Looks up the handler in MUTATION_HANDLERS (throws if unknown).
 *   2. Opens ONE Dexie 'rw' transaction over [mutation_log, …entity tables].
 *   3. For optimistic-write types, runs handler.applyOptimistic(tx, payload, key).
 *   4. Appends the row to mutation_log with status='pending'.
 *   5. Fires triggerDrain() fire-and-forget — never throws to caller.
 *   6. Re-throws if the transaction fails (caller can toast).
 *
 * Atomicity: if applyOptimistic throws, the log row is rolled back too.
 */
export function useLocalMutation() {
  const mutate = useCallback(async ({ type, payload }) => {
    const handler = MUTATION_HANDLERS[type];
    if (!handler) throw new Error(`Unknown mutation type: ${type}`);

    const key = generateMutationKey();
    const created_at = Date.now();

    // Declare all tables that may be written so Dexie can lock them atomically.
    // OVERLAY types only need mutation_log; OPTIMISTIC_WRITE types touch entity tables too.
    const tableNames =
      handler.layer === MUTATION_LAYER.OPTIMISTIC_WRITE
        ? ['mutation_log', 'articles', 'highlights']
        : ['mutation_log'];

    let priorState = null;
    await db.transaction('rw', tableNames.map((t) => db.table(t)), async (tx) => {
      if (handler.layer === MUTATION_LAYER.OPTIMISTIC_WRITE && handler.applyOptimistic) {
        const result = await handler.applyOptimistic(tx, payload, key);
        if (result?.prior_state) priorState = result.prior_state;
      }
      const entry = {
        key,
        type,
        payload,
        status: MUTATION_STATUS.PENDING,
        created_at,
        attempts: 0,
        ...(priorState ? { prior_state: priorState } : {}),
      };
      await tx.table('mutation_log').put(entry);
    });

    // Fire-and-forget; never throws to caller.
    try { triggerDrain(); } catch { /* swallow */ }

    return { key };
  }, []);

  return { mutate };
}

export default useLocalMutation;
